/**
 * DuplicateGroupCompareDialog
 *
 * Modal host for the duplicate-group comparison (sales, products, any
 * future duplicate domain). Owns the group-detail fetch, the detector
 * summary strip and the ignore lifecycle; rendering is delegated to the
 * generic `GroupComparisonTable` + a per-domain schema, so adding a new
 * object kind is one schema + one fetch branch.
 *
 * Header carries identity only (Difference-icon badge, group title, short
 * static descriptor). The detector verdict (band + top score + match
 * reasons) renders as a context strip at the top of the body.
 *
 * Ignore (Evo/PSC + action_policy gated) is select-then-confirm: members
 * are TICKED via the table's Ignore checkboxes, and the footer button -
 * whose label tracks the selection ("Ignore 2 as non-duplicates") -
 * confirms with a mandatory reason (`ReasonDialog`). Ticking every member
 * is the only way to close the whole group (deliberate per-member review,
 * no blanket ignore-all). Ignored members keep their column (chip flips
 * to Ignored, checkbox drops); a full-group ignore closes the dialog.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import DifferenceIcon from '@mui/icons-material/Difference'

import BaseDialog from '../../../shared/dialogs/BaseDialog'
import ReasonDialog from '../dialogs/ReasonDialog'
import GroupComparisonTable from './GroupComparisonTable'
import { saleComparisonSchema } from './schemas/saleComparisonSchema'
import { productComparisonSchema } from './schemas/productComparisonSchema'
import type {
  ComparisonMemberBase,
  GroupComparisonSchema,
} from './comparisonTypes'
import { isOpenMemberStatus } from './comparisonTypes'
import useDuplicateGroupDetail, {
  DuplicateGroupKind,
} from '../../../../hooks/useDuplicateGroupDetail'
import { useExceptionLifecycle } from '../../../../hooks/useExceptionLifecycle'
import { useNotify } from '../../../shared/ToastProvider'
import { useAuth } from '../../../../contexts/AuthContext'
import { canBlockExceptions } from '../../../../services/auth'
import { poolwerxColors } from '../../../../theme/colors'

export interface DuplicateGroupCompareDialogProps {
  open: boolean
  onClose: () => void
  fpCode: string
  groupId: string | null
  kind: DuplicateGroupKind
  /** Group identity for the title (e.g. representative invoice / name). */
  title?: string
  /** Host refetch after any applied ignore (group counts change behind). */
  onAfterMutation?: () => void
}

/** One reason-dialog submission target: the tag ids + display titles.
 *  `closesGroup` = the selection covers every open member, so a full
 *  success closes the dialog. */
interface ReasonTarget {
  tagIds: number[]
  titles: string[]
  closesGroup: boolean
}

// Group-level detector verdict (band + top score + match reasons),
// rendered as a context strip at the top of the BODY - it's data about
// the group, not dialog identity, so it stays out of the header.
const GroupSummaryStrip: React.FC<{
  band?: string | null
  topScore?: number | null
  matchReasons?: string[]
}> = ({ band, topScore, matchReasons }) => {
  if (!band && topScore == null && !matchReasons?.length) return null
  const high = band === 'highly_likely' || band === 'definite'
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 2, pt: 2 }}
    >
      {band && (
        <Chip
          size="small"
          variant="filled"
          label={high ? 'Highly likely' : 'Likely'}
          color={high ? 'warning' : 'info'}
        />
      )}
      {topScore != null && (
        <Typography variant="caption" color="text.secondary">
          Top match score {topScore}%
        </Typography>
      )}
      {(matchReasons ?? []).map((reason, i) => (
        <Chip
          key={`${reason}-${i}`}
          size="small"
          variant="outlined"
          label={reason.replace(/_/g, ' ')}
          sx={{ height: 20, fontSize: 11 }}
        />
      ))}
    </Stack>
  )
}

/** Members whose duplicate tag is still open (and not locally ignored). */
const collectOpenTargets = <M extends ComparisonMemberBase>(
  members: M[],
  schema: GroupComparisonSchema<M>,
  ignored: ReadonlySet<number>,
): Array<{ tagId: number; title: string }> =>
  members
    .filter(
      (m) =>
        m.tag_id != null
        && isOpenMemberStatus(m.tag_status)
        && !ignored.has(m.tag_id),
    )
    .map((m) => ({ tagId: m.tag_id as number, title: schema.memberTitle(m) }))

const DuplicateGroupCompareDialog: React.FC<DuplicateGroupCompareDialogProps> = ({
  open,
  onClose,
  fpCode,
  groupId,
  kind,
  title,
  onAfterMutation,
}) => {
  const { productData, saleData, loading, error, groupClosed } =
    useDuplicateGroupDetail(open ? fpCode : undefined, groupId, kind)
  const response = kind === 'sale' ? saleData : productData

  const { user } = useAuth()
  const { pending, bulkTransition } = useExceptionLifecycle()
  const { showError, showSuccess } = useNotify()

  // Members ignored during this dialog session - their column stays
  // visible (chip flips, checkbox drops); a fresh open starts clean.
  const [ignoredTagIds, setIgnoredTagIds] = useState<Set<number>>(new Set())
  // Members TICKED for ignoring (checkbox selection); the footer button
  // confirms the set.
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set())
  const [reasonTarget, setReasonTarget] = useState<ReasonTarget | null>(null)
  useEffect(() => {
    if (open) {
      setIgnoredTagIds(new Set())
      setSelectedTagIds(new Set())
      setReasonTarget(null)
    }
  }, [open, groupId])

  // Role gate (Evo/PSC) AND the type's action_policy. Server re-enforces.
  const actionsGated = canBlockExceptions(user) && response?.allow_ignore !== false

  const openTargets = useMemo(() => {
    if (kind === 'sale') {
      return collectOpenTargets(
        saleData?.members ?? [],
        saleComparisonSchema,
        ignoredTagIds,
      )
    }
    return collectOpenTargets(
      productData?.members ?? [],
      productComparisonSchema,
      ignoredTagIds,
    )
  }, [kind, saleData, productData, ignoredTagIds])

  const toggleMember = (tagId: number | null | undefined) => {
    if (tagId == null) return
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) next.delete(tagId)
      else next.add(tagId)
      return next
    })
  }

  // The ticked subset of open members - drives the footer label + the
  // ReasonDialog item list.
  const selectedTargets = useMemo(
    () => openTargets.filter((t) => selectedTagIds.has(t.tagId)),
    [openTargets, selectedTagIds],
  )

  const confirmSelection = () =>
    setReasonTarget({
      tagIds: selectedTargets.map((t) => t.tagId),
      titles: selectedTargets.map((t) => t.title),
      closesGroup: selectedTargets.length === openTargets.length,
    })

  const submitReason = async (reason: string) => {
    if (!reasonTarget) return
    const { tagIds, closesGroup } = reasonTarget
    try {
      const res = await bulkTransition(tagIds, 'ignore', reason)
      if (res.failed === 0 && closesGroup) {
        showSuccess(
          `All ${tagIds.length} ignored as non-duplicates - group closed.`,
        )
        setReasonTarget(null)
        onClose()
      } else if (res.failed === 0) {
        // Partial-group ignore: flip the members locally (columns stay for
        // comparison context) and clear the selection.
        setIgnoredTagIds((prev) => {
          const next = new Set(prev)
          tagIds.forEach((id) => next.add(id))
          return next
        })
        setSelectedTagIds(new Set())
        showSuccess(
          tagIds.length === 1
            ? 'Ignored as non-duplicate.'
            : `${tagIds.length} ignored as non-duplicates.`,
        )
        setReasonTarget(null)
      } else {
        // Some rows failed: flip only the successes so the user sees
        // exactly what's left, keep the dialog open.
        const okIds = res.results.filter((r) => r.ok).map((r) => r.tag_id)
        setIgnoredTagIds((prev) => {
          const next = new Set(prev)
          okIds.forEach((id) => next.add(id))
          return next
        })
        setSelectedTagIds(new Set())
        showError(`${res.failed} of ${tagIds.length} could not be ignored.`)
        setReasonTarget(null)
      }
      onAfterMutation?.()
    } catch (e: any) {
      // Keep the ReasonDialog open so the reason isn't lost on a retry.
      showError(e?.message || 'Failed to ignore - please try again.')
    }
  }

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        size="xl"
        title={title ? `Compare Duplicates - ${title}` : 'Compare Duplicates'}
        titleAttr={title}
        subtitle={
          kind === 'sale'
            ? 'Suspected duplicate sales compared side by side'
            : 'Suspected duplicate products compared side by side'
        }
        // Same Difference glyph as the table button, in the standard tinted
        // badge - ties the dialog back to the affordance that opened it.
        leadingIcon={
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.5,
              backgroundColor: poolwerxColors.tealLight,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DifferenceIcon sx={{ color: poolwerxColors.teal }} />
          </Box>
        }
        contentSx={{ p: 0 }}
        footer={
          <>
            {actionsGated && openTargets.length > 0 && (
              // Confirms the checkbox selection; the label tracks it live.
              // Disabled until at least one member is ticked - ticking all
              // is the only path to closing the whole group.
              <Button
                color="warning"
                variant="outlined"
                disabled={pending || selectedTargets.length === 0}
                onClick={confirmSelection}
              >
                {selectedTargets.length === 0
                  ? 'Ignore selected'
                  : selectedTargets.length === 1
                    ? 'Ignore 1 as non-duplicate'
                    : selectedTargets.length === openTargets.length
                      ? `Ignore all ${selectedTargets.length} as non-duplicates`
                      : `Ignore ${selectedTargets.length} as non-duplicates`}
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </>
        }
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && groupClosed && (
          <Alert severity="info" sx={{ m: 2 }}>
            This duplicate group has no open members - every item has been
            resolved or ignored.
          </Alert>
        )}
        {!loading && !error && !groupClosed && response && (
          <GroupSummaryStrip
            band={response.band}
            topScore={response.top_score}
            matchReasons={response.match_reasons}
          />
        )}
        {!loading && !error && !groupClosed && kind === 'sale' && saleData && (
          <GroupComparisonTable
            fpCode={fpCode}
            members={saleData.members}
            schema={saleComparisonSchema}
            allowIgnoreMembers={actionsGated}
            ignoredTagIds={ignoredTagIds}
            selectedTagIds={selectedTagIds}
            onToggleMember={(m) => toggleMember(m.tag_id)}
            selectionDisabled={pending}
          />
        )}
        {!loading
          && !error
          && !groupClosed
          && kind === 'product'
          && productData && (
            <GroupComparisonTable
              fpCode={fpCode}
              members={productData.members}
              schema={productComparisonSchema}
              allowIgnoreMembers={actionsGated}
              ignoredTagIds={ignoredTagIds}
              selectedTagIds={selectedTagIds}
              onToggleMember={(m) => toggleMember(m.tag_id)}
              selectionDisabled={pending}
            />
          )}
      </BaseDialog>
      {/* Mandatory-reason capture for both single and bulk ignore. Mounted
        * after the BaseDialog so it portals above it (DOM order). */}
      <ReasonDialog
        open={!!reasonTarget}
        variant="ignore"
        count={reasonTarget?.tagIds.length ?? 1}
        exceptionTypeName={
          kind === 'sale' ? 'Duplicate sale' : 'Duplicate product'
        }
        itemTitle={
          reasonTarget?.tagIds.length === 1 ? reasonTarget.titles[0] : undefined
        }
        items={
          reasonTarget && reasonTarget.tagIds.length > 1
            ? reasonTarget.titles
            : undefined
        }
        pending={pending}
        onCancel={() => setReasonTarget(null)}
        onSubmit={submitReason}
      />
    </>
  )
}

export default DuplicateGroupCompareDialog
